import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface Route {
  id: string;
  name: string;
  description?: string;
  length_miles?: number;
  difficulty?: 'easy' | 'moderate' | 'hard' | 'extreme';
  created_by?: string;
  geometry?: any;
  created_at: string;
}

export interface RouteSegment {
  id: string;
  route_id: string;
  trail_segment_id: string;
  segment_order: number;
}

export const useRoutes = () => {
  const [routes, setRoutes] = useState<Route[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRoutes = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase
          .from('routes')
          .select('*')
          .order('created_at', { ascending: false });

        if (fetchError) {
          throw fetchError;
        }

        setRoutes(data || []);
      } catch (err) {
        console.error('Error fetching routes:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch routes');
      } finally {
        setIsLoading(false);
      }
    };

    fetchRoutes();
  }, []);

  return { routes, isLoading, error };
};

export const useRouteById = (routeId: string | undefined) => {
  const [route, setRoute] = useState<Route | null>(null);
  const [segments, setSegments] = useState<RouteSegment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!routeId) {
      setIsLoading(false);
      return;
    }

    const fetchRouteDetails = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Fetch route
        const { data: routeData, error: routeError } = await supabase
          .from('routes')
          .select('*')
          .eq('id', routeId)
          .maybeSingle();

        if (routeError) {
          throw routeError;
        }

        setRoute(routeData);

        // Fetch route segments
        if (routeData) {
          const { data: segmentsData, error: segmentsError } = await supabase
            .from('route_segments')
            .select('*')
            .eq('route_id', routeId)
            .order('segment_order');

          if (segmentsError) {
            throw segmentsError;
          }

          setSegments(segmentsData || []);
        }
      } catch (err) {
        console.error('Error fetching route details:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch route details');
      } finally {
        setIsLoading(false);
      }
    };

    fetchRouteDetails();
  }, [routeId]);

  return { route, segments, isLoading, error };
};